import React, { useEffect, useMemo, useState } from "react";
import KdsUpcomingCourses from "./KdsUpcomingCourses";

export const resolveKdsColumnCount = (width) => {
  const safeWidth = Math.max(0, Number(width) || 0);
  if (safeWidth >= 1600) return 4;
  if (safeWidth >= 1100) return 3;
  if (safeWidth >= 720) return 2;
  return 1;
};

export const distributeKdsServices = (services = [], columnCount = 1) => {
  const safeColumnCount = Math.max(1, Math.floor(Number(columnCount) || 1));
  const columns = Array.from({ length: safeColumnCount }, () => []);
  (Array.isArray(services) ? services : []).forEach((service, index) => {
    columns[index % safeColumnCount].push(service);
  });
  return columns;
};

const readViewportWidth = () =>
  typeof window === "undefined" ? 0 : window.innerWidth;

export const groupKdsServices = (activeOrders = [], upcomingCourses = []) => {
  const services = new Map();
  const ensure = (order = {}) => {
    const key =
      Number(order?.orderId) > 0
        ? `order:${Number(order.orderId)}`
        : `legacy:${order?.table || order?.id || "unknown"}`;
    if (!services.has(key)) {
      services.set(key, {
        key,
        table: order?.table || null,
        serverName: order?.serverName || null,
        activeOrders: [],
        upcomingCourses: [],
      });
    }
    const service = services.get(key);
    if (!service.table && order?.table) service.table = order.table;
    if (!service.serverName && order?.serverName) {
      service.serverName = order.serverName;
    }
    return service;
  };

  (Array.isArray(activeOrders) ? activeOrders : []).forEach((order) => {
    ensure(order).activeOrders.push(order);
  });
  (Array.isArray(upcomingCourses) ? upcomingCourses : []).forEach((course) => {
    ensure(course).upcomingCourses.push(course);
  });

  const mergeUpcomingCourses = (courses = []) => {
    const merged = new Map();
    courses.forEach((course) => {
      const key = Number(course?.courseId) > 0
        ? `course:${Number(course.courseId)}`
        : `number:${Number(course?.courseNumber) || 1}`;
      if (!merged.has(key)) {
        merged.set(key, { ...course, items: [] });
      }
      const target = merged.get(key);
      const knownItemIds = new Set(
        target.items.map((item) => String(item?.id ?? item?.order_item_id ?? "")),
      );
      (course.items || []).forEach((item) => {
        const itemKey = String(item?.id ?? item?.order_item_id ?? "");
        if (itemKey && knownItemIds.has(itemKey)) return;
        target.items.push(item);
        if (itemKey) knownItemIds.add(itemKey);
      });
      if (String(course?.dispatchKind || "").toLowerCase() === "addition") {
        target.hasAddition = true;
      }
    });
    return [...merged.values()];
  };

  return [...services.values()]
    .map((service) => ({
      ...service,
      activeOrders: service.activeOrders.sort(
        (left, right) =>
          Number(left?.courseNumber || 1) - Number(right?.courseNumber || 1) ||
          Number(left?.dispatchEventId || 0) -
            Number(right?.dispatchEventId || 0),
      ),
      upcomingCourses: mergeUpcomingCourses(service.upcomingCourses).sort(
        (left, right) =>
          Number(left?.courseNumber || 1) - Number(right?.courseNumber || 1),
      ),
    }))
    .sort((left, right) =>
      String(left.table || "").localeCompare(String(right.table || ""), "fr", {
        numeric: true,
      }),
    );
};

const KdsServiceBoard = ({
  activeOrders = [],
  upcomingCourses = [],
  renderOrder,
  subtleTextClass = "text-white/70",
}) => {
  const services = useMemo(
    () => groupKdsServices(activeOrders, upcomingCourses),
    [activeOrders, upcomingCourses],
  );
  const [columnCount, setColumnCount] = useState(() =>
    resolveKdsColumnCount(readViewportWidth()),
  );

  useEffect(() => {
    const updateColumnCount = () =>
      setColumnCount(resolveKdsColumnCount(readViewportWidth()));
    updateColumnCount();
    window.addEventListener("resize", updateColumnCount);
    return () => window.removeEventListener("resize", updateColumnCount);
  }, []);

  const serviceColumns = useMemo(
    () => distributeKdsServices(services, columnCount),
    [columnCount, services],
  );

  return (
    <div
      className="grid items-start gap-5"
      style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
      data-testid="kds-service-columns"
      data-column-count={columnCount}
    >
      {serviceColumns.map((column, columnIndex) => (
        <div
          key={`kds-column-${columnIndex}`}
          className="flex min-w-0 flex-col gap-5"
          data-testid={`kds-service-column-${columnIndex}`}
        >
          {column.map((service) => (
            <section
              key={service.key}
              className="rounded-2xl border border-white/10 bg-white/[0.025] p-3 sm:p-4"
              data-kds-service-key={service.key}
            >
              <header className="mb-3 flex flex-wrap items-center justify-between gap-2 px-1">
                <strong className="text-base">
                  {service.table
                    ? `Table ${service.table}`
                    : "Commande production"}
                </strong>
                {service.serverName && service.serverName !== "N/A" && (
                  <span className={`text-sm ${subtleTextClass}`}>
                    Serveur : {service.serverName}
                  </span>
                )}
              </header>
              {service.activeOrders.length > 0 && (
                <div className="flex flex-col gap-4">
                  {service.activeOrders.map((order) => renderOrder(order))}
                </div>
              )}
              {service.upcomingCourses.length > 0 && (
                <KdsUpcomingCourses
                  courses={service.upcomingCourses}
                  subtleTextClass={subtleTextClass}
                  embedded
                />
              )}
            </section>
          ))}
        </div>
      ))}
    </div>
  );
};

export default KdsServiceBoard;
